import { tailwindColors } from '@repo/tailwind-config/tailwind.vars'
import Accordion from '@repo/ui/accordion'
import Box from '@repo/ui/box'
import { DepartureIcon, ListApprovedIcon, PinIcon, TravelTwoIcon } from '@repo/ui/icons'
import Typography from '@repo/ui/typography'
import { useQuery } from '@tanstack/react-query'
import { useState } from 'react'
import { useTranslation } from 'react-i18next'
import { useNavigate } from 'react-router-dom'

import { constructAffectedStaffPath } from '../../../../../../../packages/toolkit/src/utils/functions/construct-path'
import { getAffectedStaff } from '../../../../api/affected-staffs/get-affected-staff'
import { getAffectStaffDispatchPlaces } from '../../../../api/affected-staffs/get-affected-staff-trip-bookings'
import { Paths } from '../../../../lib/constants/path'
import { useDataProps } from '../../../../lib/hooks/use-data-props'
import { TripStepTypeEnum } from '../../../../lib/types/enums/trip-bookings/trip-step-type.enum'
import { AffectedStaff } from '../../../../lib/types/models/affectedStaff/affected-staff'
import { TripProperties } from '../../../../lib/types/models/trip/properties.trip'
import { ItemNotFound } from '../../../elements/item-not-found'
import LoadingScreen from '../../../elements/loading/loading-screen'
import TransportPicto from '../../../elements/transport-picto'
import { TravelScheduleProps } from './travel-schedule.types'
import {
  TravelScheduleDispatchPlaceCard,
  TravelScheduleDispatchPlaceDetailedCard
} from './travel-schedule-dispatch-place-card'

type AccordionState = {
  dispatchPlaces: boolean
  checklist: boolean
  groupItinerary: boolean
}

export function FrenchStaffTravelSchedulePage(props: TravelScheduleProps) {
  const { t } = useTranslation()
  const navigate = useNavigate()
  const { id, data, isPending } = useDataProps<AffectedStaff, unknown>(props, (id) =>
    getAffectedStaff(id, 'FULL')
  )
  const [accordionState, setAccordionState] = useState<AccordionState>({
    dispatchPlaces: true,
    checklist: true,
    groupItinerary: true
  })

  function handleAccordionToggle(key: keyof AccordionState) {
    setAccordionState((prev) => ({ ...prev, [key]: !prev[key] }))
  }

  const { data: dispatchPlaces } = useQuery({
    queryKey: ['affected-staff-dispatch-places', id, 'sorted-inbound-asc'],
    queryFn: () => getAffectStaffDispatchPlaces(id, { filter: { dispatched: true } }),
    enabled: !!id && !isNaN(id)
  })

  const inboundDispatchPlaces = dispatchPlaces?.filter(
    (dp) => dp.directionality === TripStepTypeEnum.INBOUND
  )
  const outboundDispatchPlaces = dispatchPlaces?.filter(
    (dp) => dp.directionality === TripStepTypeEnum.OUTBOUND
  )
  const firstInboundDispatchPlace = inboundDispatchPlaces?.[0]
  const lastOutboundDispatchPlace = outboundDispatchPlaces?.[outboundDispatchPlaces.length - 1]

  const tripLabel = TripProperties.getTripLabel(
    firstInboundDispatchPlace?.tripBooking?.relatedTrip
  )
  const tripPicto = firstInboundDispatchPlace?.tripBooking?.transportMeans?.picto

  if (isPending) return <LoadingScreen />
  if (!data) return <ItemNotFound title="Aucun staff trouvé" />
  return (
    <div className="space-y-4">
      {/* Outbound / Return trip */}
      <Accordion
        variant="primary"
        display={data.travelChoiceDiffused}
        open={accordionState.dispatchPlaces}
        onToggle={() => handleAccordionToggle('dispatchPlaces')}
        title={
          <Typography variant="h3">
            {t('staff.personal.space.travel.schedule.my.meeting.points')}
          </Typography>
        }
      >
        <div className="space-y-4">
          <div className="space-y-2">
            <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
              {t('staff.personal.space.travel.schedule.french.leader.meetings.instructions.one')}
            </Typography>

            <div>
              <Typography
                className="inline"
                variant="secondary-body"
                color={tailwindColors.grey.DEFAULT}
              >
                {t('staff.personal.space.travel.schedule.french.leader.meetings.instructions.two')}
              </Typography>
              <Typography.Link
                className="ml-1 inline"
                variant="secondary-body"
                color={tailwindColors.vividBlue.DEFAULT}
                onClick={() =>
                  navigate(
                    constructAffectedStaffPath(
                      Paths.STAFF_PERSONAL_SPACE_JOURNEY_DETAILS_EXPENSES_NOTE,
                      id
                    )
                  )
                }
              >
                {t(
                  'staff.personal.space.travel.schedule.french.leader.meetings.instructions.attention'
                )}
              </Typography.Link>
            </div>
          </div>

          {data.travelInformationDiffused &&
          (firstInboundDispatchPlace || lastOutboundDispatchPlace) ? (
            <Box style="border">
              <div className="flex items-center gap-2">
                {tripPicto && <TransportPicto picto={tripPicto} className="bg-primary-light" />}
                <Typography variant="sub-heading" color={tailwindColors.primary.DEFAULT}>
                  {tripLabel}
                </Typography>
              </div>

              <div className="grid grid-cols-2 gap-4 mobile:grid-cols-1 tab:grid-cols-1">
                {firstInboundDispatchPlace && (
                  <TravelScheduleDispatchPlaceCard
                    dispatchPlace={firstInboundDispatchPlace}
                    hasReturnTrip={!!lastOutboundDispatchPlace}
                  />
                )}
                {lastOutboundDispatchPlace && (
                  <TravelScheduleDispatchPlaceCard
                    dispatchPlace={lastOutboundDispatchPlace}
                    hideTripStepDetails
                  />
                )}
              </div>
            </Box>
          ) : (
            <Box style="border">
              <Typography variant="secondary-body">
                {t('staff.personal.space.travel.schedule.schedules.not.available.yet')}
              </Typography>
            </Box>
          )}
        </div>
      </Accordion>

      {/* Static text */}
      <Box style="border" className="bg-white-hover">
        <div className="flex items-center gap-2">
          <div className="w-10 h-10 rounded flex items-center justify-center">
            <PinIcon className="w-8 h-8" />
          </div>
          <Typography variant="secondary-body" color={tailwindColors.grey.DEFAULT}>
            {t(
              'staff.personal.space.travel.schedule.reminder.about.the.expense.reimbursement.policy'
            )}
          </Typography>
        </div>
      </Box>

      {/* Checklist of things to prepare on the day of departure */}
      <Accordion
        variant="primary"
        open={accordionState.checklist}
        onToggle={() => handleAccordionToggle('checklist')}
        title={
          <div className="flex items-center gap-2 mr-3">
            <ListApprovedIcon
              color={tailwindColors.primary.DEFAULT}
              className="bg-primary-light rounded p-1 min-w-8 size-8"
            />
            <Typography variant="sub-heading" className="text-blue-dark-hover">
              {t('staff.personal.space.travel.schedule.checklist.title')}
            </Typography>
          </div>
        }
      >
        <div className="bg-red-light p-6 rounded-3xl mobile:p-2 tab:p-2 relative">
          <div className="w-full m-auto max-w-[800px] absolute z-0 left-1/2 -translate-x-1/2">
            <img src="/waiting-passengers.png" alt="waiting-passengers" />
          </div>

          <div className="h-[330px] mobile:h-[150px]"></div>

          <Box style="shadow" className="rounded-2xl space-y-2 z-20 relative">
            <Box
              style="border"
              className="border-red-light flex flex-row gap-2 p-2 items-center rounded-lg"
            >
              <DepartureIcon
                color={tailwindColors.red.DEFAULT}
                className="bg-red-light rounded p-1 min-w-8 size-8"
              />

              <Typography variant="sub-heading" color={tailwindColors.grey.DEFAULT}>
                {t('staff.personal.space.travel.schedule.checklist.subtitle')}
              </Typography>
            </Box>

            <div className="space-y-3">
              <div className="space-y-2">
                <Typography variant="sub-heading">
                  🔹{t('staff.personal.space.travel.schedule.checklist.item.one')} :
                </Typography>

                <div className="ml-5 flex flex-col gap-1 [&>div]:flex [&>div]:flex-row [&>div]:items-center [&>div]:gap-1 [&_img]:size-8">
                  <div>
                    <img src="/box-checked.png" alt="box-checked" />

                    <div>
                      <Typography variant="sub-heading" className="inline">
                        {t('staff.personal.space.travel.schedule.checklist.item.one.one.title')} :
                      </Typography>

                      <Typography variant="sub-heading" className="inline !font-light ml-1">
                        {t('staff.personal.space.travel.schedule.checklist.item.one.one.details')}
                      </Typography>
                    </div>
                  </div>

                  <div>
                    <img src="/box-checked.png" alt="box-checked" />

                    <div>
                      <Typography variant="sub-heading" className="inline">
                        {t('staff.personal.space.travel.schedule.checklist.item.one.two.title')} :
                      </Typography>

                      <Typography variant="sub-heading" className="inline !font-light ml-1">
                        {t('staff.personal.space.travel.schedule.checklist.item.one.two.details')}
                      </Typography>
                    </div>
                  </div>

                  <div>
                    <img src="/box-checked.png" alt="box-checked" />

                    <div>
                      <Typography variant="sub-heading" className="inline">
                        {t('staff.personal.space.travel.schedule.checklist.item.one.three.title')}{' '}
                        :
                      </Typography>

                      <Typography variant="sub-heading" className="inline !font-light ml-1">
                        {t(
                          'staff.personal.space.travel.schedule.checklist.item.one.three.details'
                        )}
                      </Typography>
                    </div>
                  </div>

                  <div>
                    <img src="/box-checked.png" alt="box-checked" />

                    <div>
                      <Typography variant="sub-heading" className="inline">
                        {t('staff.personal.space.travel.schedule.checklist.item.one.four.title')} :
                      </Typography>

                      <Typography variant="sub-heading" className="inline !font-light ml-1">
                        {t('staff.personal.space.travel.schedule.checklist.item.one.four.details')}
                      </Typography>
                    </div>
                  </div>
                </div>
              </div>

              <div>
                <Typography variant="sub-heading" className="inline">
                  🔹 {t('staff.personal.space.travel.schedule.checklist.item.two')} :{' '}
                </Typography>

                <Typography variant="sub-heading" className="inline !font-light">
                  {t('staff.personal.space.travel.schedule.checklist.item.two.details')}
                </Typography>
              </div>

              <Typography variant="sub-heading">
                🔹 {t('staff.personal.space.travel.schedule.checklist.item.three')}
              </Typography>

              <Typography variant="sub-heading">
                🔹 {t('staff.personal.space.travel.schedule.checklist.item.four')}
              </Typography>

              <Typography variant="sub-heading">
                🔹 {t('staff.personal.space.travel.schedule.checklist.item.five')}
              </Typography>
            </div>
          </Box>
        </div>
      </Accordion>

      {/* Group itinerary that I am supervising → This should not be developed yet. */}
      <Accordion
        variant="primary"
        display={data.tripFolderDiffused}
        open={accordionState.groupItinerary}
        onToggle={() => handleAccordionToggle('groupItinerary')}
        title={
          <div className="flex items-center gap-2 mr-3">
            <TravelTwoIcon
              color={tailwindColors.primary.DEFAULT}
              className="bg-primary-light rounded p-1 min-w-8 size-8"
            />
            <Typography variant="sub-heading" className="text-blue-dark-hover">
              {t('staff.personal.space.travel.schedule.group.itinerary.title')}
            </Typography>
          </div>
        }
      >
        <div className="space-y-4">
          {!firstInboundDispatchPlace || !lastOutboundDispatchPlace ? (
            <Typography variant="secondary-body">
              {t('staff.personal.space.travel.schedule.group.itinerary.no.reservations')}
            </Typography>
          ) : (
            <>
              {firstInboundDispatchPlace && (
                <TravelScheduleDispatchPlaceDetailedCard
                  directionality={TripStepTypeEnum.INBOUND}
                  dispatchPlace={firstInboundDispatchPlace}
                  hasReturnTrip={!!lastOutboundDispatchPlace}
                />
              )}
              {lastOutboundDispatchPlace && (
                <TravelScheduleDispatchPlaceDetailedCard
                  directionality={TripStepTypeEnum.OUTBOUND}
                  dispatchPlace={lastOutboundDispatchPlace}
                />
              )}
            </>
          )}
        </div>
      </Accordion>
    </div>
  )
}
